import { Search } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { LABEL_CLASS, SELECT_CLASS } from "../report-styles";
import { TOP_OPTIONS, type TopOption } from "../report-utils";

interface Props {
  month: string;
  months: string[];
  onMonthChange: (month: string) => void;
  top: TopOption;
  onTopChange: (top: TopOption) => void;
  search: string;
  onSearchChange: (search: string) => void;
  searchLabel: string;
  onExport: () => void;
  exportDisabled: boolean;
  children?: React.ReactNode;
}

export const ReportControls = ({
  month,
  months,
  onMonthChange,
  top,
  onTopChange,
  search,
  onSearchChange,
  searchLabel,
  onExport,
  exportDisabled,
  children,
}: Props) => {
  const { formatMonthYear } = useDateFormatter();
  const options = months.includes(month) ? months : [month, ...months];
  return (
    <div className="flex flex-wrap items-center gap-4" role="group" aria-label="Report controls">
      <label className={LABEL_CLASS}>
        Month
        <select
          aria-label="Month"
          value={month}
          onChange={event => onMonthChange(event.target.value)}
          className={SELECT_CLASS}
        >
          {options.map(option => (
            <option key={option} value={option}>
              {formatMonthYear(option)}
            </option>
          ))}
        </select>
      </label>
      {children}
      <label className={LABEL_CLASS}>
        Show
        <select
          aria-label="Show top"
          value={top}
          onChange={event => onTopChange(event.target.value as TopOption)}
          className={SELECT_CLASS}
        >
          {TOP_OPTIONS.map(option => (
            <option key={option} value={option}>
              {option === "all" ? "All" : `Top ${option}`}
            </option>
          ))}
        </select>
      </label>
      <label className="relative min-w-60 flex-1">
        <span className="sr-only">{searchLabel}</span>
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-500"
          aria-hidden="true"
        />
        <input
          type="search"
          aria-label={searchLabel}
          placeholder={searchLabel}
          value={search}
          maxLength={200}
          onChange={event => onSearchChange(event.target.value)}
          className="h-9 w-full rounded-md border border-spend-field bg-white pl-9 pr-3 text-sm text-gray-800 placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </label>
      <Button type="button" onClick={onExport} disabled={exportDisabled}>
        Export CSV
      </Button>
    </div>
  );
};
